import { api } from './api';
import { API_ENDPOINTS } from '../config/api';

// ============================================================================
// Types
// ============================================================================

interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

interface PortalSessionResponse {
  portal_url: string;
}

interface SessionVerificationResponse {
  status: string;
  payment_status: string;
  mode: string;
  customer_email: string | null;
  amount_total: number | null;
  currency: string | null;
}

interface SubscriptionData {
  id: number;
  stripe_subscription_id: string;
  plan_key: string;
  status: string;
  is_active: boolean;
  is_valid: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  monthly_credits: number;
  created_at: string | null;
}

interface SubscriptionStatusResponse {
  credit_balance: number;
  subscription: SubscriptionData | null;
  has_active_subscription: boolean;
}

interface CreditTransaction {
  id: number;
  amount: number;
  balance_after: number;
  transaction_type: string;
  reason: string;
  reference_type: string | null;
  reference_id: string | null;
  created_at: string;
}

interface CreditHistoryResponse {
  transactions: CreditTransaction[];
  current_balance: number;
}

interface CreditBalanceResponse {
  credit_balance: number;
}

export interface InvoiceData {
  id: number;
  stripe_invoice_id: string;
  amount_paid_display: number;
  currency: string;
  status: string;
  invoice_pdf_url: string | null;
  hosted_invoice_url: string | null;
  paid_at: string | null;
  created_at: string | null;
}

interface InvoiceListResponse {
  invoices: InvoiceData[];
}

// ============================================================================
// Service
// ============================================================================

export const subscriptionService = {
  /**
   * Start checkout session for a subscription plan
   */
  async startCheckout(planKey: string): Promise<CheckoutSessionResponse> {
    const { data } = await api.post<CheckoutSessionResponse>(
      API_ENDPOINTS.subscriptions.checkout,
      { plan_key: planKey }
    );
    return data;
  },

  /**
   * Start checkout session for one-time credit purchase
   */
  async startCreditCheckout(credits: number, label?: string): Promise<CheckoutSessionResponse> {
    const { data } = await api.post<CheckoutSessionResponse>(
      API_ENDPOINTS.subscriptions.creditsCheckout,
      { credits, label }
    );
    return data;
  },

  /**
   * Open Stripe customer portal for subscription management
   */
  async openCustomerPortal(): Promise<PortalSessionResponse> {
    const { data } = await api.post<PortalSessionResponse>(API_ENDPOINTS.subscriptions.portal);
    return data;
  },

  /**
   * Verify a checkout session status
   */
  async verifySession(sessionId: string): Promise<SessionVerificationResponse> {
    const { data } = await api.get<SessionVerificationResponse>(
      API_ENDPOINTS.subscriptions.verifySession(sessionId)
    );
    return data;
  },

  /**
   * Get current user's subscription and credit status
   */
  async getStatus(): Promise<SubscriptionStatusResponse> {
    const { data } = await api.get<SubscriptionStatusResponse>(API_ENDPOINTS.subscriptions.status);
    return data;
  },

  /**
   * Get credit transaction history
   */
  async getCreditHistory(limit = 50, offset = 0): Promise<CreditHistoryResponse> {
    const { data } = await api.get<CreditHistoryResponse>(API_ENDPOINTS.subscriptions.creditHistory, {
      params: { limit, offset },
    });
    return data;
  },

  /**
   * Get current credit balance
   */
  async getCreditBalance(): Promise<number> {
    const { data } = await api.get<CreditBalanceResponse>(API_ENDPOINTS.subscriptions.creditBalance);
    return data.credit_balance;
  },

  /**
   * Get invoices for current user
   */
  async getInvoices(): Promise<InvoiceData[]> {
    const { data } = await api.get<InvoiceListResponse>(API_ENDPOINTS.subscriptions.invoices);
    return data.invoices;
  },
};

export default subscriptionService;

// Export types for use in components
export type {
  CheckoutSessionResponse,
  PortalSessionResponse,
  SessionVerificationResponse,
  SubscriptionData,
  SubscriptionStatusResponse,
  CreditTransaction,
  CreditHistoryResponse,
};
